import { gql, useQuery } from '@apollo/client';
import { CustomCard } from '@app/shared/components';
import { AppAlert, useAppState } from '@itrustcapital/ui';
import { Spinner, Text } from '@ui-kitten/components';
import {
	EditAccountScreen_GetTreasuryAccount,
	EditAccountScreen_GetTreasuryAccountVariables,
} from 'codegen/EditAccountScreen_GetTreasuryAccount';
import React from 'react';
import { StyleSheet, View } from 'react-native';

export interface KYBProps {
	accountId: number;
}

export const EDIT_ACCOUNT_SCREEN_GET_TREASURY_ACCOUNT = gql`
	query EditAccountScreen_GetTreasuryAccount($where: AccountFilterInput!) {
		accounts(where: $where, first: 1) {
			nodes {
				treasuryAccount {
					kybStatus
					alloyId
					name
					employerIdentificationNumber
					type
				}
			}
		}
	}
`;

export function KYB(props: KYBProps) {
	const styles = useCustomStyles();

	const errorVisible = useAppState(false);

	const getTreasuryAccountDetailsQuery = useQuery<
		EditAccountScreen_GetTreasuryAccount,
		EditAccountScreen_GetTreasuryAccountVariables
	>(EDIT_ACCOUNT_SCREEN_GET_TREASURY_ACCOUNT, {
		variables: {
			where: {
				id: {
					eq: props.accountId,
				},
			},
		},
		skip: !props.accountId,
	});

	const [account] = getTreasuryAccountDetailsQuery?.data?.accounts?.nodes || [];

	return (
		<>
			<CustomCard
				header={() => (
					<View style={styles.main.header}>
						<Text category="h6">KYB</Text>
					</View>
				)}
			>
				<AppAlert
					actions={[
						{
							title: 'OK',
							status: 'danger',
							onPress: () => errorVisible.set(false),
						},
					]}
					message="Sorry, something went wrong. Please try again later."
					title="Request Failed"
					visible={errorVisible.get}
				/>

				<View
					style={{
						flexDirection: 'row',
						justifyContent: 'space-between',
					}}
				>
					<View>
						<Text style={styles.main.statusHeader}>name:</Text>
						<Text style={styles.main.status}>
							{getTreasuryAccountDetailsQuery?.loading ? (
								<Spinner size="tiny" status="basic" />
							) : (
								account?.treasuryAccount?.name || 'N/A'
							)}
						</Text>
					</View>
					<View>
						<Text style={styles.main.statusHeader}>EIN:</Text>
						<Text style={styles.main.status}>
							{getTreasuryAccountDetailsQuery?.loading ? (
								<Spinner size="tiny" status="basic" />
							) : (
								account?.treasuryAccount?.employerIdentificationNumber || 'N/A'
							)}
						</Text>
					</View>
					<View>
						<Text style={styles.main.statusHeader}>type:</Text>
						<Text style={styles.main.status}>
							{getTreasuryAccountDetailsQuery?.loading ? (
								<Spinner size="tiny" status="basic" />
							) : (
								account?.treasuryAccount?.type || 'N/A'
							)}
						</Text>
					</View>
					<View>
						<Text style={styles.main.statusHeader}>status:</Text>
						<Text style={styles.main.status}>
							{getTreasuryAccountDetailsQuery?.loading ? (
								<Spinner size="tiny" status="basic" />
							) : (
								account?.treasuryAccount?.kybStatus || 'N/A'
							)}
						</Text>
					</View>
					<View>
						<Text style={styles.main.statusHeader}>alloy id:</Text>
						<Text style={styles.main.status}>
							{getTreasuryAccountDetailsQuery?.loading ? (
								<Spinner size="tiny" status="basic" />
							) : (
								account?.treasuryAccount?.alloyId || 'N/A'
							)}
						</Text>
					</View>
				</View>
			</CustomCard>
		</>
	);
}

function useCustomStyles() {
	return {
		main: StyleSheet.create({
			header: {
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
			},
			statusHeader: {
				fontVariant: ['small-caps'],
			},
			status: {
				fontSize: 16,
				fontWeight: '800',
				textTransform: 'uppercase',
			},
		}),
		tags: StyleSheet.create({
			container: {
				flex: 1,
				flexDirection: 'row',
				flexWrap: 'wrap',
			},
			group: {
				flexDirection: 'row',
			},
			seperator: {
				paddingHorizontal: 8,
			},
		}),
	};
}
